"use client";

import { useEditor, EditorContent } from "@tiptap/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { stripTrailingEmptyParagraphs } from "../../../lib/sceneContentBlocks";
import { getRichTextEditorExtensions } from "./lib/extensions";
import type { DefaultAlignment } from "./lib/extensions";
import { cn } from "@/lib/utils";
import "./richTextEditor.css";
import { useRichTextToolbar } from "./richTextToolbarContext";

export interface RichTextEditorProps {
    /** Initial HTML content. */
    content?: string;
    /** Called when content changes (HTML string). */
    onUpdate?: (html: string) => void;
    /** Placeholder when empty. */
    placeholder?: string;
    /** Show the formatting bar when editor is focused (default true). When false, bar is hidden. */
    showFormattingBarOnFocus?: boolean;
    /** Default alignment for new paragraphs (center for A/B/C, start for D/E/F). */
    defaultAlignment?: DefaultAlignment;
    /** Editor container class name. */
    className?: string;
    /** Minimum height of the editable area. */
    minHeight?: string;
    /** Disable editing. */
    editable?: boolean;
    /** Focus the editor when it becomes editable (canvas text-edit entry). */
    autoFocus?: boolean;
    /** Viewport point to land the caret at on focus (click-to-edit). When set,
     *  overrides the default "focus at end". */
    focusCaret?: { x: number; y: number } | null;
    /** Called once the pending caret point has been applied. */
    onCaretConsumed?: () => void;
    /** Notified when the editor gains/loses focus (canvas selection sync). */
    onFocusChange?: (focused: boolean) => void;
}

/** Tiptap-based rich text editor with a second topbar for formatting (like recorder). */
export function RichTextEditor({
    content = "",
    onUpdate,
    placeholder = "Write something…",
    showFormattingBarOnFocus = true,
    defaultAlignment = "center",
    className,
    minHeight = "1.5em",
    editable = true,
    autoFocus = false,
    focusCaret = null,
    onCaretConsumed,
    onFocusChange,
}: RichTextEditorProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const lastEmittedContentRef = useRef<string | null>(null);
    const extensions = useMemo(
        () => getRichTextEditorExtensions(defaultAlignment),
        [defaultAlignment]
    );
    const {
        activate,
        deactivate,
        editor: activeToolbarEditor,
        toolbarRoot,
    } = useRichTextToolbar();

    const canShowToolbar = showFormattingBarOnFocus && editable;

    const editor = useEditor({
        extensions,
        content: content || undefined,
        editable,
        immediatelyRender: false,
        editorProps: {
            attributes: {
                class: "rich-text-editor-content focus:outline-none min-w-0 w-full min-h-[1.5em]",
                "data-placeholder": placeholder,
            },
        },
        onUpdate: ({ editor: e }) => {
            const html = e.getHTML();
            const normalized = stripTrailingEmptyParagraphs(html);
            lastEmittedContentRef.current = normalized;
            onUpdate?.(normalized);
        },
    });

    // Activate toolbar on focus (only for editable editors that want the toolbar).
    useEffect(() => {
        if (!editor || !canShowToolbar) return;

        const handleFocus = () => activate(editor);

        editor.on("focus", handleFocus);
        return () => {
            editor.off("focus", handleFocus);
            deactivate(editor);
        };
    }, [editor, canShowToolbar, activate, deactivate]);

    // Deactivate toolbar only when user clicks outside editor + toolbar.
    useEffect(() => {
        if (!editor || !canShowToolbar) return;

        const handleDocumentPointerDown = (e: PointerEvent) => {
            if (activeToolbarEditor !== editor) return;
            const target = e.target as Node;
            if (!target || !(target instanceof HTMLElement)) return;
            if (rootRef.current?.contains(target)) return;
            if (toolbarRoot?.contains(target)) return;
            if (target.closest("[data-state=open]")) return;
            // Any open Radix popper (Select dropdown, Popover, DropdownMenu)
            // means this click is dismissing it — not deactivating the editor.
            // Capture-phase fires before Radix tears down the popper, so its
            // wrapper is still in the DOM at this moment.
            if (
                document.querySelector("[data-radix-popper-content-wrapper]")
            ) {
                return;
            }
            // Block-image edit mode in progress (user clicked an imageGrid
            // image, then Replace, and is now picking from the media sidebar).
            // useMediaActions needs this editor to write the new url back to
            // the selected node, so keep it active until the flow ends.
            if (document.querySelector("[data-block-image-bar]")) {
                return;
            }
            deactivate(editor);
            // A genuine click outside the editor + toolbar also leaves canvas
            // edit mode (the formatting toolbar and radix poppers are excluded
            // above, so this never fires while interacting with them).
            onFocusChange?.(false);
        };

        document.addEventListener(
            "pointerdown",
            handleDocumentPointerDown,
            true
        );
        return () =>
            document.removeEventListener(
                "pointerdown",
                handleDocumentPointerDown,
                true
            );
    }, [
        editor,
        activeToolbarEditor,
        toolbarRoot,
        canShowToolbar,
        deactivate,
        onFocusChange,
    ]);

    // Sync element selection when the editor gains focus. We deliberately do
    // NOT exit edit mode on blur: clicking the formatting toolbar blurs the
    // contenteditable, and that must not collapse the bar. Edit mode ends via
    // a genuine outside click (above), Escape, or selecting another element.
    useEffect(() => {
        if (!editor || !onFocusChange) return;
        const onFocus = () => onFocusChange(true);
        editor.on("focus", onFocus);
        return () => {
            editor.off("focus", onFocus);
        };
    }, [editor, onFocusChange]);

    // Keep the live editor's editable flag in sync with the prop (canvas text
    // elements toggle between display and edit mode without remounting).
    useEffect(() => {
        if (editor && editor.isEditable !== editable) {
            editor.setEditable(editable);
        }
    }, [editor, editable]);

    // Focus once when entering edit mode on the canvas. If a caret point was
    // supplied (click-to-edit), land the caret there; otherwise focus at end.
    // The ref guards against re-running when `focusCaret` is cleared.
    const focusedForEditRef = useRef(false);
    useEffect(() => {
        if (!editor) return;
        if (!(editable && autoFocus)) {
            focusedForEditRef.current = false;
            return;
        }
        if (focusedForEditRef.current) return;
        focusedForEditRef.current = true;
        if (focusCaret) {
            const at = editor.view.posAtCoords({
                left: focusCaret.x,
                top: focusCaret.y,
            });
            if (at) {
                editor.chain().focus().setTextSelection(at.pos).run();
            } else {
                editor.commands.focus("end");
            }
            onCaretConsumed?.();
        } else {
            editor.commands.focus("end");
        }
    }, [editor, editable, autoFocus, focusCaret, onCaretConsumed]);

    // Sync content prop → editor (for scene switches, external updates).
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const normalizedCurrent = current === "<p></p>" ? "" : current;
        const normalizedContent = content || "";
        const isOurUpdate = normalizedContent === lastEmittedContentRef.current;
        if (!isOurUpdate && normalizedContent !== normalizedCurrent) {
            editor.commands.setContent(normalizedContent, {
                emitUpdate: false,
            });
            lastEmittedContentRef.current = normalizedContent;
        }
    }, [content, editor]);

    return (
        <div
            ref={rootRef}
            className={cn(
                "flex flex-col w-full rounded-[8px] transition-[box-shadow] ring-inset",
                className
            )}
            data-rich-text-editor
        >
            <div className="overflow-visible min-w-0" style={{ minHeight }}>
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}
